import { FormErrorMessage, FormHelperText, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormControl, Input } from '@time-line/components/common';
import { SIGN_UP_CTA_LABEL } from 'components/heros/AnchorCtas/constants';
import type { ColorScheme } from 'components/heros/PromotionHero';
import { useListSignup } from 'hooks/useListSignup';
import { useSubscribeSignup } from 'hooks/useSubscribeSignup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { SubscribeMarketingCheckbox } from './SubscribeMarketingCheckbox';
import { SubscribeTerms } from './SubscribeTerms';

const validationSchema = z.object({
  email: z.email({ error: 'Email is invalid' }),
  marketingOptIn: z.boolean(),
});

type FormSchema = z.infer<typeof validationSchema>;

interface SubscribeEmailProps {
  colorScheme: ColorScheme;
  buttonColorScheme: ColorScheme;
  klaviyoListId?: string; // SHOP-8528: will be mandatory
  handleSuccess?: (email: string, emailConsent: boolean) => void;
  ctaTitle?: string;
  autoSubscribeMarketing?: boolean | null;
}

export function SubscribeEmail({
  colorScheme,
  buttonColorScheme,
  klaviyoListId,
  handleSuccess,
  ctaTitle = SIGN_UP_CTA_LABEL,
  autoSubscribeMarketing,
}: SubscribeEmailProps) {
  const subscribeAutomatically = autoSubscribeMarketing ?? true;
  const [isSubscribed, setIsSubscribed] = useState(false);
  //  SHOP-8528: remove
  const { mutateAsync: mutateAsyncOld, isPending: isPendingOld } =
    useSubscribeSignup();

  const { mutateAsync, isPending } = useListSignup();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormSchema>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      marketingOptIn: false,
    },
  });

  const onSubmit = async ({ email, marketingOptIn }: FormSchema) => {
    const emailConsent = subscribeAutomatically || marketingOptIn;
    const source = window.location.pathname;

    try {
      await mutateAsync(
        {
          email,
          source,
          listId: klaviyoListId as string, // SHOP-8528: remove 'as string'
          emailConsent,
        },
        {
          onSuccess: () => {
            setIsSubscribed(true);
            handleSuccess?.(email, emailConsent);
          },
        },
      );
    } catch (err) {
      if (err instanceof Error) {
        setError('email', {
          type: 'manual',
          message: err.message,
        });
      }
    }
  };

  // SHOP-8528: Remove and just use onSubmit
  const onSubmitOld = async ({ email, marketingOptIn }: FormSchema) => {
    const emailConsent = subscribeAutomatically || marketingOptIn;
    if (!emailConsent) return;

    const path = new URL(window.location.href).pathname;

    try {
      await mutateAsyncOld(
        { email, attributes: { slug: path } },
        {
          onSuccess: () => {
            setIsSubscribed(true);
            handleSuccess?.(email, emailConsent);
          },
        },
      );
    } catch (err) {
      if (err instanceof Error) {
        setError('email', {
          type: 'manual',
          message: err.message,
        });
      }
    }
  };

  const isSuccess = isSubscribed && !errors.email;

  return (
    <Stack spacing="3xs">
      <form onSubmit={handleSubmit(klaviyoListId ? onSubmit : onSubmitOld)}>
        <FormControl isInvalid={!!errors.email}>
          <>
            <Input
              type="email"
              autoComplete="email"
              placeholder="Email"
              colorScheme={colorScheme}
              {...register('email')}
            />

            <FormErrorMessage
              fontSize="2xs"
              color={colorScheme === 'white' ? 'red.100' : 'red.300'}
            >
              {errors.email?.message}
            </FormErrorMessage>

            {isSuccess && (
              <FormHelperText fontSize="2xs" color="green.600">
                Success!
              </FormHelperText>
            )}

            <Button
              type="submit"
              isLoading={isPending || isPendingOld}
              width="100%"
              mt="2xs"
              colorScheme={buttonColorScheme}
            >
              {ctaTitle}
            </Button>
          </>
        </FormControl>
        {!subscribeAutomatically && (
          <SubscribeMarketingCheckbox
            colorScheme={colorScheme}
            {...register('marketingOptIn')}
          />
        )}
      </form>
      {subscribeAutomatically && (
        <SubscribeTerms consentType="email" colorScheme={colorScheme} />
      )}
    </Stack>
  );
}
